import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import PhoneApp from '../../../../assets/images/phone-app.png';
import { browserStorage } from '../../../../utils/browserStorage.util';
import { postMessageToRN } from '../../../../utils/RNCommunicator/postMessageToRN.util';
import { vibrateDefault } from '../../../../utils/vibration.util';
import { DimmedBackground } from '../../../common/dimmed/DimmedBackground';

interface ICallGuideModalProps {
  isShow: boolean;
  virtualNumber: string | null;
  onClose: () => void;
  imageUrl?: string;
  shouldCheckFrequency?: boolean; // 노출 빈도 체크 여부 (기본값: true)
}

const REDIRECT_DELAY_MS = 4000; // 전화 앱으로 이동하는 시간 (4초)
const REDIRECT_DELAY_SECONDS = REDIRECT_DELAY_MS / 1000; // 초 단위로 변환
const SEVEN_DAYS_IN_MS = 7 * 24 * 60 * 60 * 1000; // 7일 (604,800,000 ms)

export const CallGuideModal = ({
  isShow,
  virtualNumber,
  onClose,
  imageUrl,
  shouldCheckFrequency = true,
}: ICallGuideModalProps) => {
  const hasStartedTimerRef = useRef(false);
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(REDIRECT_DELAY_SECONDS);

  // 모달을 표시해도 되는지 확인하는 함수
  const checkShouldShowModal = useCallback(() => {
    if (!shouldCheckFrequency) {
      return true;
    }

    const lastViewedStr = browserStorage.getItem({
      storageType: 'localStorage',
      itemKey: 'lastCallGuideModalView',
    });

    if (!lastViewedStr) {
      return true; // 처음 보는 경우
    }

    const lastViewedTime = parseInt(lastViewedStr, 10);
    const currentTime = new Date().getTime();
    const timeDiff = currentTime - lastViewedTime;

    return timeDiff >= SEVEN_DAYS_IN_MS; // 7일이 지났는지 확인
  }, [shouldCheckFrequency]);

  // 모달을 본 시간을 저장하는 함수
  const saveLastViewTime = useCallback(() => {
    if (!shouldCheckFrequency) {
      return;
    }

    const currentTime = new Date().getTime().toString();
    browserStorage.setItem({
      storageType: 'localStorage',
      itemKey: 'lastCallGuideModalView',
      value: currentTime,
    });
  }, [shouldCheckFrequency]);

  // isShow가 변경될 때 노출 빈도를 체크
  useEffect(() => {
    if (isShow) {
      const canShow = checkShouldShowModal();
      setShouldShowModal(canShow);

      if (canShow) {
        saveLastViewTime();
      } else {
        // 노출 빈도 조건을 만족하지 않으면 모달 없이 바로 전화 앱으로 이동
        onClose();
        if (virtualNumber) {
          if (window.ReactNativeWebView) {
            postMessageToRN({ messageKey: 'link', messageParam: `tel:${virtualNumber}` });
          } else {
            window.location.href = `tel:${virtualNumber}`;
          }
        }
      }
    } else {
      setShouldShowModal(false);
    }
  }, [isShow, checkShouldShowModal, saveLastViewTime, onClose, virtualNumber]);

  useEffect(() => {
    // 모달이 닫히거나 표시 조건을 만족하지 않으면 타이머 시작 플래그 리셋 및 카운트다운 리셋
    if (!isShow || !shouldShowModal) {
      hasStartedTimerRef.current = false;
      setRemainingSeconds(REDIRECT_DELAY_SECONDS);
      return undefined;
    }

    // 모달이 열리고 virtualNumber가 있고 아직 타이머를 시작하지 않았으면
    if (!virtualNumber || hasStartedTimerRef.current) {
      return undefined;
    }

    hasStartedTimerRef.current = true;

    // 카운트다운 초기화
    setRemainingSeconds(REDIRECT_DELAY_SECONDS);

    // 모달이 열릴 때 진동 실행
    vibrateDefault();

    console.log(`타이머 시작: ${REDIRECT_DELAY_SECONDS}초 후 모달 닫기 + 전화 앱 열기`);

    // 카운트다운 타이머 (1초마다 실행)
    const countdownInterval = setInterval(() => {
      setRemainingSeconds((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(countdownInterval);
        }
        return next;
      });
    }, 1000);

    // 실제 전화 앱 이동 타이머
    const redirectTimer = setTimeout(() => {
      console.log('모달 닫기 + 전화 앱 열기:', virtualNumber);
      // 모달 닫기
      onClose();
      // 전화 앱 열기
      if (window.ReactNativeWebView) {
        postMessageToRN({ messageKey: 'link', messageParam: `tel:${virtualNumber}` });
      } else {
        window.location.href = `tel:${virtualNumber}`;
      }
    }, REDIRECT_DELAY_MS);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(redirectTimer);
    };
  }, [isShow, shouldShowModal, virtualNumber, onClose]);

  if (!isShow || !shouldShowModal) {
    return null;
  }

  return (
    <DimmedBackground isShow={isShow && shouldShowModal}>
      <Container
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <BodyContainer>
          <TitleDescriptionContainer>
            <TitleContainer>
              <Title>{'☎️ 판매자와 전화하기'}</Title>
            </TitleContainer>
            <Description>{'잠시 후 전화 앱으로 연결 됩니다'}</Description>
          </TitleDescriptionContainer>
          <ImageContainer>
            <ImageWrapper>
              <Image
                src={PhoneApp}
                alt={'통화 앱 안내'}
                onClick={() => ampli.pvmClickInquiryIntroduceModalPhoneAppImage()}
              />
            </ImageWrapper>
            <Description>{`${remainingSeconds}초 후 다음 화면이 열리면\n‘통화 버튼’을 누르세요!`}</Description>
          </ImageContainer>
        </BodyContainer>
      </Container>
    </DimmedBackground>
  );
};

const Container = styled.div`
  width: 300px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  padding: 24px 24px 32px 24px;

  gap: 24px;

  position: absolute;
  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  border-radius: var(--radius-large, 16px);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const BodyContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h1`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
  white-space: pre-wrap;
`;

const Description = styled.pre`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;

  white-space: pre-wrap;
`;

const Image = styled.img`
  width: 204px;
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f2f3f5;
  border-radius: 8px;
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
`;

const TitleDescriptionContainer = styled.div`
  display: flex;
  padding-bottom: 24px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  align-self: stretch;
`;

const TitleContainer = styled.div`
  display: flex;
  padding-top: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
`;

const ImageContainer = styled.div`
  display: flex;
  max-height: 400px;
  padding-bottom: 8px;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;

const ImageWrapper = styled.div`
  display: flex;
  padding: 0 24px;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  border-radius: var(--radius-large, 16px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;
